import React from 'react';
import { Typography, Box, Divider, Stack } from '@mui/material';
import { formatDuration } from '../../../../utils/formatter';
import ArtistLink from '../../../../components/common/ArtistLink';
import theme from '../../../../theme';

const TrackRow = ({ track, showArtist }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      width: '100%',
      minHeight: showArtist ? 53 : 33,
      p: 0.75,
    }}
  >
    <Typography
      color={theme.palette.black[2]}
      fontSize={14}
      sx={
        track.trackNumber >= 10
          ? {
              ml: '-0.55em',
              transform: 'translateX(0.15em)',
              fontVariantNumeric: 'tabular-nums',
            }
          : undefined
      }
    >
      {track.trackNumber}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          color={theme.palette.black[6]}
          fontSize={{ xs: 13, md: 14 }}
          lineHeight={'normal'}
          sx={{ minWidth: 0, wordBreak: 'break-word' }}
        >
          {track.title}
        </Typography>
        {track.contentRating === 'EXPLICIT' && (
          <Box
            component="svg"
            viewBox="0 0 38 38"
            sx={{
              width: { xs: '10px', md: '11px' },
              height: { xs: '10px', md: '11px' },
              alignSelf: 'center',
              flexShrink: 0,
            }}
          >
            <path
              d="M33.5833 0.25H4.41667C2.125 0.25 0.25 2.125 0.25 4.41667V33.5833C0.25 35.875 2.125 37.75 4.41667 37.75H33.5833C35.875 37.75 37.75 35.875 37.75 33.5833V4.41667C37.75 2.125 35.875 0.25 33.5833 0.25ZM25.25 12.75H16.9167V16.9167H25.25V21.0833H16.9167V25.25H25.25V29.4167H12.75V8.58333H25.25V12.75Z"
              fill="rgb(112,112,112)"
            />
          </Box>
        )}
      </Box>
      {showArtist && track.artistName && (
        <ArtistLink
          artistName={track.artistName}
          artistSource={
            track.artistSource || (track.artistSourceId ? 'APPLE_MUSIC' : track.albumSource)
          }
          artistSourceId={track.artistSourceId}
          artistSlug={track.artistSlug}
          artistDbId={track.artistId}
          sx={{
            color: theme.palette.black[2],
            fontSize: { xs: 13, md: 14 },
            mt: 0.5,
            WebkitLineClamp: 1,
          }}
        />
      )}
    </Box>
    <Typography color={theme.palette.black[2]} fontSize={{ xs: 13, md: 14 }} sx={{ flexShrink: 0 }}>
      {formatDuration(track.durationMs)}
    </Typography>
  </Box>
);

const Tracklist = ({ tracks }) => {
  const sortedTracks = tracks
    ? [...tracks].sort(
        (a, b) => (a.discNumber ?? 1) - (b.discNumber ?? 1) || a.trackNumber - b.trackNumber,
      )
    : [];

  const hasMultipleArtists =
    sortedTracks.length > 0 &&
    new Set(sortedTracks.map((t) => t.artistName).filter(Boolean)).size > 1;

  const isMultiDisc =
    sortedTracks.length > 0 && sortedTracks.some((t) => (t.discNumber ?? 1) !== 1);

  const discGroups = isMultiDisc
    ? sortedTracks.reduce((acc, track) => {
        const disc = track.discNumber ?? 1;
        if (!acc[disc]) acc[disc] = [];
        acc[disc].push(track);
        return acc;
      }, {})
    : null;

  return (
    <Box>
      <Typography
        color={theme.palette.black[9]}
        fontSize={{ xs: 16, md: 18, lg: 18 }}
        fontWeight={'bold'}
        mb={1}
      >
        트랙리스트
      </Typography>
      <Divider sx={{ borderColor: theme.palette.grey.line }} />
      {isMultiDisc ? (
        Object.entries(discGroups).map(([discNum, discTracks]) => (
          <Box key={`disc-${discNum}`} mt={{ xs: 1.25, md: 1.5, lg: 1.5 }}>
            <Typography
              color={theme.palette.black[9]}
              fontSize={{ xs: 14, md: 16 }}
              fontWeight={'bold'}
              pt={'6px'}
              pb={'6px'}
            >
              Disc {discNum}
            </Typography>
            <Stack>
              {discTracks.map((track, index) => (
                <React.Fragment key={`${track.id}-${index}`}>
                  <Divider sx={{ borderColor: theme.palette.grey.bg }} />
                  <TrackRow track={track} showArtist={hasMultipleArtists} />
                  {index === discTracks.length - 1 && (
                    <Divider sx={{ borderColor: theme.palette.grey.bg }} />
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        ))
      ) : (
        <Stack mt={{ xs: 0.5, md: 1.5, lg: 1.5 }}>
          {sortedTracks.map((track, index) => (
            <React.Fragment key={`${track.id}-${index}`}>
              {index > 0 && <Divider sx={{ borderColor: theme.palette.grey.bg }} />}
              <TrackRow track={track} showArtist={hasMultipleArtists} />
              {index === sortedTracks.length - 1 && (
                <Divider sx={{ borderColor: theme.palette.grey.bg }} />
              )}
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Tracklist;
